import { useEffect, useState, createContext, FC, useContext } from "react";
import { LDClient, LDContext, initialize } from "launchdarkly-react-client-sdk";
import { Brand, UserType } from "utils/models/CommonTypes";
import { useConfig } from "../config";
import {
  FeatureFlags,
  FeatureFlagKey,
  FeatureFlagProviderProps,
  FeatureFlagValue,
} from "./FeatureFlagProvider.types";
import { useAuth } from "../auth/AuthProvider";

const featureFlagKeys = Object.values(FeatureFlagKey);

const createDefaultFeatureFlags = (): FeatureFlags => {
  return featureFlagKeys.reduce((acc, key) => {
    return { ...acc, [key]: false };
  }, {} as FeatureFlags);
};

const mapFlagsToFeatureFlags = (flags: Record<string, any>): FeatureFlags => {
  return Object.fromEntries(
    Object.entries(flags)
      .filter(([key]) => featureFlagKeys.includes(key as FeatureFlagKey))
      .map(([key, value]) => [key as FeatureFlagKey, Boolean(value)])
  ) as FeatureFlags;
};

const processFeatureFlagUpdates = (
  updates: Record<string, any>,
  prevFlags: FeatureFlags
): FeatureFlags => {
  return { ...prevFlags, ...mapFlagsToFeatureFlags(updates) };
};

const createLDUserContext = (
  brand: Brand,
  appVersion: string,
  appBuildNumber: string,
  user?: UserType | null | undefined
): LDContext => {
  const hostname = window.location.hostname;
  const formatBrand = brand.toLowerCase();

  return {
    kind: "user",
    key: "anonymous",
    anonymous: true,
    hostname,
    appVersion,
    appBuildNumber,
    brand: formatBrand,
  };
};

const FeatureFlagContext = createContext<FeatureFlags>(
  createDefaultFeatureFlags()
);

export const FeatureFlagProvider: FC<FeatureFlagProviderProps> = ({
  children,
}) => {
  const { LAUNCH_DARKLY_KEY, BRAND, BUILD_NUMBER, VERSION } = useConfig();
  const { user } = useAuth();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(
    createDefaultFeatureFlags()
  );
  const [ldClient, setLdClient] = useState<LDClient | null>(null);

  useEffect(() => {

    if (!LAUNCH_DARKLY_KEY) return;

    const client = initialize(
      LAUNCH_DARKLY_KEY,
      createLDUserContext(BRAND, VERSION, BUILD_NUMBER, null)
    );
    setLdClient(client);
    const handleReady = () => {
      const flags = client.allFlags();
      setFeatureFlags(mapFlagsToFeatureFlags(flags));
    };

    const handleChange = (updates: Record<string, any>) => {
      setFeatureFlags((prevFlags) =>
        processFeatureFlagUpdates(updates, prevFlags)
      );
    };

    client.on("ready", handleReady);
    client.on("change", handleChange);

    return () => {
      client.off("ready", handleReady);
      client.off("change", handleChange);
      client.close();
    };
  }, [BRAND, LAUNCH_DARKLY_KEY, VERSION, BUILD_NUMBER]);

  useEffect(() => {
    const updateUserContext = () => {
      if (!ldClient || !user || !user.loginID) return;

      const currentKey = ldClient?.getContext()?.key;
      if (currentKey !== user.loginID) {
        ldClient.identify(
          createLDUserContext(BRAND, VERSION, BUILD_NUMBER, user)
        );
        const flags = ldClient.allFlags();
        setFeatureFlags(mapFlagsToFeatureFlags(flags));
      }
    };

    updateUserContext();
  }, [user, ldClient, BRAND, VERSION, BUILD_NUMBER]);

  return (
    <FeatureFlagContext.Provider value={featureFlags}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = (flag: FeatureFlagKey): boolean => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlag must be used within a FeatureFlagProvider");
  }

  const flagValue = context[flag];

  if (flagValue === undefined) {
    console.warn(`Feature flag for ${flag} is not available.`);
    return false;
  }

  return flagValue;
};
